import moment from 'moment';
import { Patient } from '@/models/patient';

/**
 * Gets the patient's full name.
 * @param patient The patient object.
 * @returns The full name or an empty string.
 */
export const getPatientFullName = (patient: Patient | null): string => {
  if (!patient) { return ''; }
  const fullName = patient.fullName?.trim();
  return fullName || '';
};

/**
 * Gets the patient's kana name (Family + Given).
 * @param patient The patient object.
 * @returns The kana name or an empty string.
 */
export const getPatientKanaName = (patient: Patient | null): string => {
  if (!patient) { return ''; }
  const familyKana = patient.family_name_kana;
  const givenKana = patient.given_name_kana;
  if (!familyKana && !givenKana) { return ''; }
  return `${familyKana || ''} ${givenKana || ''}`.trim();
};

/**
 * Gets the formatted birthday display string with age.
 * @param patient The patient object.
 * @returns Formatted string or an empty string.
 */
export const getPatientBirthdayDisplay = (patient: Patient | null): string => {
  if (!patient?.date_of_birth) { return ''; }
  const dob = moment(patient.date_of_birth).utcOffset(9);
  if (!dob.isValid()) { return ''; }
  const age = moment().utcOffset(9).diff(dob, 'years');
  return `${dob.format('YYYY年MM月DD日')} (${age}歳)`;
};

/**
 * Finds the value of an answer based on a keyword in the question.
 * @param answers The list of patient answers.
 * @param keyword The keyword to search for.
 * @returns The answer value or an empty string.
 */
export const findAnswerValue = (answers: any[], keyword: string): string => {
  if (!answers || answers.length === 0) { return ''; }
  for (const data of answers) {
    const questionText = data.question?.question || '';
    if (questionText.includes(keyword)) {
      const answersList = data.answer || [];
      if (answersList.length > 0 && answersList[0].value) {
        return answersList[0].value;
      }
    }
  }
  return '';
};

/**
 * Gets the BMI display value.
 * @param answers The list of patient answers.
 * @param treatmentName The name of the treatment service.
 * @returns The BMI rounded to 1 decimal place or an empty string.
 */
export const getBMIDisplay = (answers: any[], treatmentName: string | undefined): string => {
  const isMedialDiet = treatmentName?.includes('メディカルダイエット');
  let heightValue = findAnswerValue(answers, '身長');
  let weightValue = findAnswerValue(answers, '体重');

  // For メディカルダイエット, also try broader keyword search
  if (isMedialDiet) {
    if (!heightValue) { heightValue = findAnswerValue(answers, 'cm'); }
    if (!weightValue) { weightValue = findAnswerValue(answers, 'kg'); }
  }

  if (!heightValue || !weightValue) { return ''; }

  const heightCm = parseFloat(heightValue);
  const weightKg = parseFloat(weightValue);
  if (!heightCm || !weightKg || heightCm <= 0 || weightKg <= 0) { return ''; }

  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);
  return bmi.toFixed(1);
};
